import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import notification from "../../helpers/notification";

// Calendar Modal Component
const CalendarDateTimePicker = ({ onDateTimeSelect, resetTrigger }) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const modalRef = useRef(null);

  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentlySelected, setCurrentlySelected] = useState(null);
  const [selectedTime, setSelectedTime] = useState({
    hour: 12,
    minute: 0,
    period: "AM",
  });

  const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // remove time portion

    const days = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const dayDate = new Date(year, month - 1, daysInPrevMonth - i);
      dayDate.setHours(0, 0, 0, 0);
      days.push({
        day: daysInPrevMonth - i,
        month: month - 1,
        year,
        isCurrentMonth: false,
        isPast: true,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      dayDate.setHours(0, 0, 0, 0);
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        isPast: dayDate < today,
      });
    }

    // Next month days
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
        isPast: false,
      });
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const handleDateClick = (day, month, year) => {
    const selected = new Date(year, month, day);
    selected.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected >= today) {
      setCurrentlySelected(selected);
    } else {
      notification("warning", t("You cannot select a past date."));
    }
  };

  const handleTimeChange = (type, value) => {
    setSelectedTime((prev) => ({ ...prev, [type]: value }));
  };

  const incrementTime = (type) => {
    if (type === "hour") {
      const newHour = selectedTime.hour < 12 ? selectedTime.hour + 1 : 1;
      setSelectedTime((prev) => ({ ...prev, hour: newHour }));
    } else if (type === "minute") {
      const newMinute = selectedTime.minute < 59 ? selectedTime.minute + 1 : 0;
      setSelectedTime((prev) => ({ ...prev, minute: newMinute }));
    }
  };

  const decrementTime = (type) => {
    if (type === "hour") {
      const newHour = selectedTime.hour > 1 ? selectedTime.hour - 1 : 12;
      setSelectedTime((prev) => ({ ...prev, hour: newHour }));
    } else if (type === "minute") {
      const newMinute = selectedTime.minute > 0 ? selectedTime.minute - 1 : 59;
      setSelectedTime((prev) => ({ ...prev, minute: newMinute }));
    }
  };

  const togglePeriod = () => {
    setSelectedTime((prev) => ({
      ...prev,
      period: prev.period === "AM" ? "PM" : "AM",
    }));
  };

  const handleClear = () => {
    setCurrentlySelected(null);
    setSelectedTime({ hour: 12, minute: 0, period: "AM" });
  };

  const handleConfirm = () => {
    if (currentlySelected) {
      const selectedDateTime = {
        date: currentlySelected,
        time: selectedTime,
        formattedString: `${
          monthNames[currentlySelected.getMonth()]
        } ${currentlySelected.getDate()}, ${currentlySelected.getFullYear()} - ${
          selectedTime.hour
        }:${selectedTime.minute.toString().padStart(2, "0")} ${
          selectedTime.period
        }`,
      };

      onDateTimeSelect(selectedDateTime);
      setShowModal(false);
    } else {
      notification("warning", t("Please select a date"));
    }
  };

  const calendarDays = generateCalendarDays();

  useEffect(() => {
    if (resetTrigger) {
      setCurrentlySelected(null);
      setSelectedTime({ hour: 12, minute: 0, period: "AM" });
    }
  }, [resetTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModal]);

  return (
    <div className="position-relative w-100">
      {/* Date Selection Input */}
      <div
        className="row mb-3 d-flex justify-content-between cursor-pointer"
        onClick={() => setShowModal(true)}
        style={{ cursor: "pointer" }}
      >
        <div className="col-md-5 fw-medium mobile-font">
          {t("Select Date and Time")}
          <p
            style={{
              color: "#6E717FE5",
              marginTop: "2%",
              fontSize: "14px",
            }}
          >
            {t("Schedule Harinaam Sankirtan")}
          </p>
        </div>
        <div className="col-md-4 text-center">
          <div className="card shadow-sm rounded border-primary border-2 p-3">
            <div
              className="col-md-full"
              style={{
                outline: "1px solid #dee2e6", // Bootstrap primary color
                paddingTop: "8px",
                paddingBottom: "8px",
                borderRadius: "5px",
                backgroundColor: "#f8f9fa",
                fontSize: "13px",
              }}
            >
              {currentlySelected
                ? `${
                    monthNames[currentlySelected.getMonth()]
                  } ${currentlySelected.getDate()}, ${currentlySelected.getFullYear()} - 
          ${selectedTime.hour}:${selectedTime.minute
                    .toString()
                    .padStart(2, "0")} ${selectedTime.period}`
                : t("Click to select")}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Overlay */}
      {showModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)", // for Safari support
            backgroundColor: "rgba(12, 12, 12, 0.2)", // optional: light frosted look
            zIndex: 1050,
          }}
        >
          <div
            className="bg-white rounded shadow-lg overflow-hidden"
            style={{ maxWidth: "350px", width: "100%" }}
            ref={modalRef} // <-- attach the ref here
          >
            {/* Calendar Header */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <button
                onClick={handlePrevMonth}
                className="btn btn-sm btn-light"
                type="button"
              >
                <span className="fs-4">&lt;</span>
              </button>
              <div className="fs-5 fw-semibold">
                {t(monthNames[currentMonth.getMonth()])}{" "}
                {currentMonth.getFullYear()}
              </div>
              <button
                onClick={handleNextMonth}
                className="btn btn-sm btn-light"
                type="button"
              >
                <span className="fs-4">&gt;</span>
              </button>
            </div>

            {/* Calendar Days of Week */}
            <div className="d-flex justify-content-between p-3 border-bottom">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className="text-center text-secondary fw-medium"
                  style={{ width: "14.28%" }}
                >
                  {t(day)}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="p-3 border-bottom">
              <div className="d-flex flex-wrap">
                {calendarDays.map((day, index) => {
                  const isSelected =
                    currentlySelected &&
                    currentlySelected.getDate() === day.day &&
                    currentlySelected.getMonth() === day.month &&
                    currentlySelected.getFullYear() === day.year;

                  const isClickable = day.isCurrentMonth && !day.isPast;

                  return (
                    <div
                      key={index}
                      className={`
        p-2 text-center rounded-3
        ${day.isCurrentMonth ? "hover-bg-light" : "text-muted"}
        ${isSelected ? "bg-danger text-white" : ""}
        ${!isClickable ? "text-muted opacity-50" : ""}
      `}
                      onClick={() =>
                        isClickable &&
                        handleDateClick(day.day, day.month, day.year)
                      }
                      style={{
                        width: "14.28%",
                        cursor: isClickable ? "pointer" : "default",
                      }}
                    >
                      {day.day}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time Picker */}
            <div className="d-flex justify-content-center align-items-center p-3 border-bottom">
              {/* Hour picker */}
              <div className="d-flex flex-column align-items-center mx-3">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => incrementTime("hour")}
                  type="button"
                >
                  <span>▲</span>
                </button>
                <div
                  className="fs-4 fw-semibold my-1 text-center"
                  style={{ width: "40px" }}
                >
                  {selectedTime.hour}
                </div>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => decrementTime("hour")}
                  type="button"
                >
                  <span>▼</span>
                </button>
              </div>

              <div className="fs-4 fw-semibold">:</div>

              {/* Minute picker */}
              <div className="d-flex flex-column align-items-center mx-3">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => incrementTime("minute")}
                  type="button"
                >
                  <span>▲</span>
                </button>
                <div
                  className="fs-4 fw-semibold my-1 text-center"
                  style={{ width: "40px" }}
                >
                  {selectedTime.minute.toString().padStart(2, "0")}
                </div>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => decrementTime("minute")}
                  type="button"
                >
                  <span>▼</span>
                </button>
              </div>

              {/* AM/PM toggle */}
              <div className="d-flex flex-column">
                <button
                  className={`btn btn-sm text-white mb-1`}
                  style={{
                    backgroundColor:
                      selectedTime.period === "PM" ? "#9F1D21" : "grey",
                    color: selectedTime.period === "PM" ? "white" : "black",
                  }}
                  onClick={() => selectedTime.period !== "PM" && togglePeriod()}
                  type="button"
                >
                  {t("PM")}
                </button>
                <button
                  className={`btn btn-sm text-white mb-1`}
                  style={{
                    backgroundColor:
                      selectedTime.period === "AM" ? "#9F1D21" : "grey",
                    color: selectedTime.period === "AM" ? "white" : "black",
                  }}
                  onClick={() => selectedTime.period !== "AM" && togglePeriod()}
                  type="button"
                >
                  {t("AM")}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-between p-3">
              <button
                className="btn btn-outline-danger"
                onClick={handleClear}
                type="button"
              >
                {t("Clear")}
              </button>
              <button
                className="btn text-white"
                style={{ backgroundColor: "#9F1D21" }}
                onClick={handleConfirm}
                type="button"
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarDateTimePicker;
